import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import CTABanner from '@/components/shared/CTABanner';
import { DCA_LINKS } from '@/lib/dcaConfig';
import { publicApi } from '@/lib/api';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [serviceRes, settingsRes] = await Promise.all([
          publicApi.getServiceBySlug(slug),
          publicApi.getSiteSettings()
        ]);
        setService(serviceRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        setError('Service not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-heading font-bold mb-4">Service Not Found</h1>
            <p className="text-muted-foreground mb-8">The service you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/services">Back to Services</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title={service.title} 
          breadcrumbs={[
            { label: 'Services', path: '/services' },
            { label: service.title }
          ]} 
        />

        {/* Service Content */}
        <section className="py-20" data-testid="service-detail-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {service.image_url && (
                  <div className="rounded-2xl overflow-hidden mb-8">
                    <img
                      src={service.image_url}
                      alt={service.title}
                      className="w-full h-auto"
                    />
                  </div>
                )}
                
                <div className="prose prose-lg max-w-none">
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {service.full_description || service.short_description}
                  </p>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 bg-muted/30 rounded-2xl p-8">
                  <h3 className="text-xl font-heading font-bold mb-6">Ready to Get Started?</h3>
                  <p className="text-muted-foreground mb-6">
                    Schedule a complimentary consultation to discuss how we can help with your career goals.
                  </p>
                  <Button className="w-full" size="lg" data-testid="service-cta-btn" onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}>
                    Schedule Consultation <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  <div className="mt-8 pt-8 border-t">
                    <h4 className="font-heading font-bold mb-4">Contact Us</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>Phone: {settings?.phone}</li>
                      <li>Email: {settings?.email}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
